/**
 * The audit registry: every action and resource an `AuditLog` row may
 * carry, on every install (api, desktop, node). The api and the web derive
 * their types, filters and labels from it.
 *
 * `AUDIT_RESOURCE_LABELS` and `AUDIT_ACTION_LABELS` are `Record`s over the
 * registry, so a value declared without a French label does not compile.
 * `packages/api/tests/audit/audit-resource-registry.test.ts` reads every
 * `packages/<name>/src` and `apps/<name>/src` for the values written
 * (quoted, through a constant, or as a key of the desktop's queued-entry
 * list) and fails on any value missing here.
 */
/**
 * Toute ressource sur laquelle une ligne d'audit est écrite.
 *
 * L'ORDRE est celui de la lecture à l'écran : le socle de facturation
 * d'abord, puis les modules métier. Ajouter une entrée ici OBLIGE à
 * fournir son libellé français ci-dessous (le `Record` ne compile pas
 * sinon).
 */
export const AUDIT_RESOURCES = [
    // Socle facturation / administration
    "invoices",
    "clients",
    "articles",
    "users",
    "roles",
    "stock",
    // A depot moved to another site; `resourceId` is the depot and
    // `details.section` is « rattachement ».
    "stock-locations",
    "reports",
    "tenants",
    "nlp",
    // Budget (verticale ONG/bailleurs et exécution publique)
    "budget-envelopes",
    "budget-commitments",
    "budget-payments",
    "budget-parameters",
    "budget-mode-public",
    // Paie
    "payroll-parameters",
    // Arbitrage d'une signature MCF d'issue inconnue : l'opérateur déclare
    // ce que le rapport X/Z du périphérique montre. La décision engage la
    // position fiscale (un mauvais choix brûle un second compteur ou perd
    // une signature), donc elle laisse une trace comme le passer-outre.
    "mcf-signatures",
    // A PI-SPI event the retry sweep will never process automatically,
    // resolved manually — the same trail as an MCF arbitration decision.
    "pispi-events",
    // Cancellation of an imported à-nouveaux entry (contre-passation).
    "accounting-openings",
    // A journal entry written through the entries API: draft created,
    // modified or deleted, entry validated or reversed; `resourceId` is the
    // entry and `details.operation` names the act.
    "accounting-entries",
    // One row per file import (clients, articles, suppliers, opening
    // balance, employees, bank statement); `details.importKind` names it.
    "imports",
    // A till sale; its price overrides are written against it.
    "pos-transactions",
    // A fiscal number presented to the MCF, refused and cancelled by an
    // administrator; `resourceId` is the invoice that held it.
    "invoice-numbers",
    // A company VAT rate of a GENERIC_VAT jurisdiction, defined or withdrawn.
    "vat-rates",
    // A version of the tenant's document theme (#536); `resourceId` is the
    // TenantDocumentTheme row.
    "document-themes",
    // A site MCF agent: its revocation (#606).
    "mcf-agents",
    // A registered MCF of an establishment: its registration and retirement
    // (arrêté n°2025-0049, art. 6 e)).
    "mcf-devices",
    // An MCF outage: its declaration, each notification recorded, its end
    // (arrêté n°2025-0049, art. 7 c)).
    "mcf-outages",
    // An on-site node: the closures of the transitional « pas de MCF » mode
    // it received (#606).
    "nodes",
    // A write of the platform operator on a jurisdiction (obligation period,
    // suspension of the obligation), recorded in the operator's tenant;
    // `details.section` names it.
    "platform",
    // A quote; `resourceId` is the quote.
    "quotes",
    // The read-only lock of a desktop restore, lifted once the MCF shows no
    // invoice certified since the restored data (#836); one row per tenant.
    "restore-locks",
    // The SFE build an install starts with (#259): version, fingerprint,
    // homologation and ISF, written when they change for the tenant.
    "sfe-builds",
    // A numbering series continued from the last number the client's former
    // system issued; `resourceId` is the sequence row.
    "numbering-sequences",
    // A sale of the client's former invoicing system, entered, modified or
    // deleted; `resourceId` is the sale.
    "external-sales",
    // The desktop station itself (disk encryption acknowledged); no
    // `resourceId`.
    "workstation",
    // A file handed out by an export route (action EXPORT); `details.kind`
    // names the export, `details.sha256` the bytes sent.
    "exports",
    // The conversion of a company's open items at a currency switch;
    // `resourceId` is the conversion, `details.operation` names the act.
    "currency-open-items",
    // The rows below are written by the database's reference-data history
    // (prisma/sql/reference-history-*.sql); `resourceId` is the record, and
    // for `user-roles` the user holding the role.
    "establishments",
    "suppliers",
    "user-roles",
    "account-mappings",
    "accounting-journals",
    "bank-accounts",
    "employees",
];
/**
 * Libellés français (homologation DGI).
 *
 * `budget-mode-public` désigne un RÉGLAGE d'entreprise, pas une pièce —
 * sa ligne porte `resourceId = tenantId` (`accounting/smt.service.ts`) —
 * d'où un libellé qui ne promet aucun document à ouvrir.
 */
export const AUDIT_RESOURCE_LABELS = {
    invoices: "Factures",
    clients: "Clients",
    articles: "Articles",
    users: "Utilisateurs",
    roles: "Rôles",
    stock: "Stock",
    "stock-locations": "Dépôts",
    reports: "Rapports",
    tenants: "Entreprise",
    nlp: "Assistant IA",
    "budget-envelopes": "Enveloppes budgétaires",
    "budget-commitments": "Engagements budgétaires",
    "budget-payments": "Règlements budgétaires",
    "budget-parameters": "Paramètres budgétaires",
    "budget-mode-public": "Mode exécution publique",
    "payroll-parameters": "Paramètres de paie",
    "mcf-signatures": "Signatures MCF",
    "pispi-events": "Événements PI-SPI",
    "accounting-openings": "À-nouveaux",
    "accounting-entries": "Écritures comptables",
    imports: "Imports de fichiers",
    "pos-transactions": "Ventes de caisse",
    "invoice-numbers": "Numéros de facture",
    "vat-rates": "Taux de TVA",
    "document-themes": "Apparence des documents",
    "mcf-agents": "Agents MCF de site",
    "mcf-devices": "Appareils MCF",
    "mcf-outages": "Pannes de MCF",
    nodes: "Nœuds sur site",
    platform: "Plateforme",
    quotes: "Devis",
    "restore-locks": "Verrous de restauration",
    "sfe-builds": "Version du logiciel de facturation en service",
    "numbering-sequences": "Numérotation",
    "external-sales": "Ventes antérieures à la bascule",
    workstation: "Poste de travail",
    exports: "Exports de données",
    "currency-open-items": "Conversion des éléments ouverts au changement de monnaie",
    establishments: "Établissements",
    suppliers: "Fournisseurs",
    "user-roles": "Attributions de rôles",
    "account-mappings": "Affectations de comptes",
    "accounting-journals": "Journaux comptables",
    "bank-accounts": "Comptes bancaires",
    employees: "Employés",
};
/**
 * Every action an audit row may carry. Adding one here requires its French
 * label below.
 */
export const AUDIT_ACTIONS = [
    "CREATE",
    "UPDATE",
    "DELETE",
    "LOGIN",
    "LOGOUT",
    "EXPORT",
    "SIGN",
    "STATUS_CHANGE",
    "NLP_QUERY",
    "IMPORT",
    // A till line priced by the operator instead of the catalogue.
    "PRICE_OVERRIDE",
    // An invoice's original emitted a second time (rule C).
    "ORIGINAL_REISSUE",
    // A desktop print claim of an invoice's original, cancelled: its ticket
    // was handed out without a DUPLICATA mention.
    "PRINT_CLAIM_CANCELLED",
    // An install started with an SFE build other than the tenant's last one.
    "SFE_BUILD_STARTED",
    // Desktop: the user accepted to run on a disk that is not encrypted.
    "DISK_ENCRYPTION_ACKNOWLEDGED",
    // Desktop: the user accepted a read-only restore, then it was applied.
    "RESTORE_READ_ONLY_CONSENTED",
    "RESTORE_READ_ONLY_APPLIED",
    // Desktop: the restore lock of replaced data kept aside with that data,
    // and a lock put back in force with the data it concerns.
    "RESTORE_LOCK_MOVED_ASIDE",
    "RESTORE_LOCK_REINSTATED",
    // An MCF whose taxpayer information (2Bh) does not correspond to the
    // seller printed, met by a certified issue that went on.
    "MCF_TAXPAYER_MISMATCH",
    // A settlement recorded on a signed invoice, cancelled.
    "PAYMENT_CANCELLED",
];
/** Libellés français (homologation DGI). */
export const AUDIT_ACTION_LABELS = {
    CREATE: "Création",
    UPDATE: "Modification",
    DELETE: "Suppression",
    LOGIN: "Connexion",
    LOGOUT: "Déconnexion",
    EXPORT: "Export",
    SIGN: "Signature",
    STATUS_CHANGE: "Changement de statut",
    NLP_QUERY: "Requête assistant IA",
    IMPORT: "Import",
    PRICE_OVERRIDE: "Prix imposé",
    ORIGINAL_REISSUE: "Réédition de l'original",
    PRINT_CLAIM_CANCELLED: "Impression de l'original annulée",
    SFE_BUILD_STARTED: "Mise en service d'une version",
    DISK_ENCRYPTION_ACKNOWLEDGED: "Disque non chiffré accepté",
    RESTORE_READ_ONLY_CONSENTED: "Restauration en lecture seule acceptée",
    RESTORE_READ_ONLY_APPLIED: "Restauration en lecture seule appliquée",
    RESTORE_LOCK_MOVED_ASIDE: "Verrou de restauration mis de côté",
    RESTORE_LOCK_REINSTATED: "Verrou de restauration rétabli",
    MCF_TAXPAYER_MISMATCH: "Identité du contribuable du MCF différente",
    PAYMENT_CANCELLED: "Annulation de règlement",
};
//# sourceMappingURL=audit.js.map